import { forwardRef } from 'react';

import { Button } from '../../../buttons';
import { useModalContext } from '../modal-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { ModalCloseButtonProps } from './modal-close-button.types';

/**
 * This button is a convenience component that applies the recommended styling for the Cancel button in the
 * modal footer. It wraps the BrButton and sets the type to tertiary and attaches the Modal's onClose handler
 * to the button's onClick event handler, so the developer does not have to manually attach the modal dialog close
 * handler to the onClose handler of the Modal and again to the BrButton for the cancel button in the footer.
 */
const ModalCloseButton = forwardRef(
  (props: ModalCloseButtonProps, forwardedRef: ForwardedRef<HTMLButtonElement>): ReactElement => {
    const { children, className, ...rest } = props;

    const modalContext = useModalContext();
    if (modalContext === null) {
      throw new Error('ModalCloseButton must be used within a ModalContext.Provider');
    }

    const { onClose } = modalContext;

    return (
      <Button {...rest} className={className} onClick={() => onClose?.()} ref={forwardedRef} type="tertiary">
        {children}
      </Button>
    );
  },
);

ModalCloseButton.displayName = 'ModalCloseButton';

export default ModalCloseButton;
